import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Sparkles, Check, ArrowRight, Crown } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { getHistory } from "../lib/history";
import { canonicalShareUrl } from "../lib/shareUrl";
import { track } from "../lib/track";
import { useAuth } from "../lib/AuthContext";

/**
 * Exhausted — appears when a free user has poured their 3 cards for the day.
 *
 * iter-94 — the carrot:
 *   1. Show their already-made cards
 *   2. Tap any → native Web Share API kicks off
 *   3. After share, POST /me/redeem-bonus-pour with that slug
 *   4. User earns +1 bonus pour for TOMORROW (single-use)
 *
 * Also prominently offers upgrade as the unlimited path.
 */
export default function Exhausted() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isPremium } = useAuth() || {};
    const headline = location.state?.headline || "the well's quiet for tonight.";
    const advice = location.state?.advice || "you've poured everything you had today.";

    const [history, setHistory] = useState([]);
    const [redeemed, setRedeemed] = useState(false);
    const [sharingSlug, setSharingSlug] = useState(null);

    useEffect(() => {
        const items = getHistory().filter((m) => m.slug);
        setHistory(items.slice(0, 6)); // show last 6
        track("exhausted_screen_viewed", { history_count: items.length });
        // If a bonus is already pending for tomorrow, reflect it.
        axios.get(`${API}/me/quota`, { withCredentials: true })
            .then(({ data }) => { if (data?.bonus_pour_pending) setRedeemed(true); })
            .catch(() => {});
    }, []);

    const handleShareAndRedeem = async (card) => {
        if (sharingSlug || redeemed) return;
        setSharingSlug(card.slug);
        track("exhausted_share_clicked", { slug: card.slug });

        const url = canonicalShareUrl(card.slug);
        const shareText = card.preview_title || "i made a card.";

        // Try native share first; fall back to clipboard.
        let shared = false;
        try {
            if (navigator.share) {
                await navigator.share({ title: "MoodCard", text: shareText, url });
                shared = true;
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                shared = true;
            }
        } catch {
            // User likely dismissed the share sheet — still treat as eligible
            // to redeem? No — only redeem on confirmed share. So bail here.
            setSharingSlug(null);
            return;
        }

        if (!shared) {
            setSharingSlug(null);
            return;
        }

        // Redeem the bonus pour for tomorrow.
        try {
            const { data } = await axios.post(
                `${API}/me/redeem-bonus-pour`,
                { slug: card.slug },
                { withCredentials: true },
            );
            if (data?.ok) {
                setRedeemed(true);
                track("bonus_pour_redeemed", { slug: card.slug });
                try { window.dispatchEvent(new CustomEvent("quota-changed")); } catch { /* ignore */ }
            }
        } catch (e) {
            console.error("redeem failed", e);
        } finally {
            setSharingSlug(null);
        }
    };

    const openUpgrade = () => {
        track("exhausted_upgrade_clicked");
        try {
            window.dispatchEvent(new CustomEvent("open-premium-modal", { detail: { source: "exhausted_screen" } }));
        } catch { /* ignore */ }
    };

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="exhausted-screen">
            <Helmet>
                <title>One last move — MoodCard</title>
                <meta name="description" content="You poured everything today. Share an existing card to earn one bonus pour for tomorrow." />
            </Helmet>

            <div className="absolute inset-0 aurora-wash opacity-25 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <header className="relative z-10 max-w-md mx-auto px-5 pt-5 flex items-center justify-between">
                <button
                    onClick={() => navigate("/")}
                    data-testid="exhausted-back"
                    className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 hover:text-app transition-colors"
                >
                    ← home
                </button>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                    the well
                </span>
            </header>

            <main className="relative z-10 max-w-md mx-auto px-5 pt-8 pb-24">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <h1
                        className="font-serif font-light italic text-app text-3xl leading-tight tracking-tight"
                        data-testid="exhausted-headline"
                    >
                        {headline}
                    </h1>
                    <p className="mt-3 font-serif italic text-app-2 text-[15px] leading-relaxed">
                        {advice}
                    </p>
                </motion.div>

                {/* === The Carrot === */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="mt-8 rounded-2xl border border-amber-300/30 bg-amber-300/[0.05] px-5 py-5"
                    data-testid="exhausted-carrot-block"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-3.5 h-3.5 text-amber-200" strokeWidth={1.5} />
                        <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-amber-100">
                            unlock 1 bonus pour tomorrow
                        </p>
                    </div>
                    <p className="font-serif italic text-app text-[17px] leading-snug">
                        share a card you've already made.
                    </p>
                    <p className="mt-1.5 font-serif italic text-app-3 text-[12px] leading-snug">
                        someone out there might need exactly those words. and you'll earn one more pour tomorrow.
                    </p>

                    <AnimatePresence mode="wait">
                        {redeemed ? (
                            <motion.div
                                key="redeemed"
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-4 flex items-center gap-2 rounded-full bg-amber-200/15 border border-amber-200/40 px-4 py-2.5"
                                data-testid="exhausted-redeemed-confirm"
                            >
                                <Check className="w-4 h-4 text-amber-100" strokeWidth={1.8} />
                                <span className="font-sans text-[12px] text-amber-100 tracking-wide">
                                    +1 bonus pour unlocked for tomorrow
                                </span>
                            </motion.div>
                        ) : history.length === 0 ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-4"
                            >
                                <p className="font-serif italic text-app-3 text-[12px]">
                                    no past cards in this browser to share. make one tomorrow.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="cards"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-4 space-y-2"
                            >
                                {history.map((card) => (
                                    <button
                                        key={card.id}
                                        onClick={() => handleShareAndRedeem(card)}
                                        disabled={sharingSlug === card.slug}
                                        data-testid={`exhausted-share-card-${card.slug}`}
                                        className="w-full text-left rounded-xl border border-app/15 bg-surface-1 px-3.5 py-3 hover:border-app/35 transition-colors disabled:opacity-50"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 truncate">
                                                    {card.selected_style || card.preview_style || "card"}
                                                </p>
                                                {card.preview_title && (
                                                    <p className="mt-0.5 font-serif font-medium text-app text-[13px] leading-tight truncate">
                                                        {card.preview_title}
                                                    </p>
                                                )}
                                                {card.preview_body && (
                                                    <p className="mt-0.5 font-serif italic text-app-2 text-[11px] leading-snug line-clamp-2">
                                                        {card.preview_body}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex-shrink-0 self-center">
                                                {sharingSlug === card.slug ? (
                                                    <Sparkles className="w-4 h-4 text-amber-200 animate-pulse" strokeWidth={1.6} />
                                                ) : (
                                                    <Share2 className="w-4 h-4 text-app-2" strokeWidth={1.6} />
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* === Or upgrade — iter-100 illuminated, winking ===
                    iter-101 — HIDDEN for premium/plus users (they're already paid). */}
                {!isPremium && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-6"
                >
                    <motion.button
                        onClick={openUpgrade}
                        data-testid="exhausted-upgrade-button"
                        animate={{ scale: [1, 1.015, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        className="relative overflow-hidden w-full inline-flex items-center justify-between gap-3 py-4 px-5 rounded-2xl border-2 border-amber-300/55 transition-all"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(252,211,77,0.18) 0%, rgba(244,114,182,0.14) 50%, rgba(167,139,250,0.16) 100%)",
                            boxShadow:
                                "0 0 28px -6px rgba(252,211,77,0.45), inset 0 1px 0 rgba(255,255,255,0.12)",
                        }}
                    >
                        {/* Shimmer sweep — diagonal highlight wipes across every 3s
                            ("wink"). Pointer-events: none so it doesn't block clicks. */}
                        <motion.span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0"
                            initial={{ x: "-30%", opacity: 0.6 }}
                            animate={{ x: "130%", opacity: [0.6, 1, 0.6] }}
                            transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" }}
                            style={{
                                width: "30%",
                                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)",
                                filter: "blur(8px)",
                            }}
                        />
                        <div className="relative z-10 flex items-center gap-3">
                            <Crown className="w-5 h-5 text-amber-200" strokeWidth={1.6} fill="rgb(252,211,77)" />
                            <div className="text-left">
                                <p className="font-sans text-[12px] uppercase tracking-[0.26em] text-amber-100 font-semibold">
                                    pour without limits
                                </p>
                                <p className="font-serif italic text-amber-200/85 text-[12px]">
                                    $7.99/mo or $59.99/yr · 3 months free
                                </p>
                            </div>
                        </div>
                        <ArrowRight className="relative z-10 w-5 h-5 text-amber-100" strokeWidth={1.8} />
                    </motion.button>
                </motion.div>
                )}

                <p className="mt-6 text-center font-serif italic text-app-3 text-[13px]">
                    your past cards are always free to send. that part never runs out.
                </p>

                <div className="mt-4 text-center">
                    <Link to="/saved" className="font-sans text-[11px] uppercase tracking-[0.28em] text-app-2 hover:text-app transition-colors" data-testid="exhausted-history-link">
                        See My Cards →
                    </Link>
                </div>
            </main>
        </div>
    );
}
